import '../styles/PageGlobal.css';
import './ContactDetailsEditPage.css';
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ContactService } from '../services/ContactService.js';

export default function ContactDetailsEditPage() {
  const { contact_id } = useParams();
  const navigate = useNavigate();
  
  const [contact, setContact] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchContact = useCallback(async () => {
    const service = new ContactService();
    setLoading(true);
    setError(null);
    try {
      const data = await service.getContactById(contact_id);
      
      // 🟢 Extraction propre du nom réel de l'entité liée (Client ou Fournisseur)
      const entity = data.client || data.fournisseur;
      const entityName = entity 
        ? (entity.societe1 || entity.raisonSociale || entity.nom || `Entité #${entity.id}`) 
        : 'Non lié';
      
      const entityType = data.client ? 'Client' : (data.fournisseur ? 'Fournisseur' : 'Aucun');

      setContact({
        id: data.id,
        nom: data.nom || '',
        prenom: data.prenom || '',
        email1: data.email1 || '',
        email2: data.email2 || '',
        telephone1: data.telephone1 || '',
        telephone2: data.telephone2 || '',
        societe: entityName, // 🟢 Le vrai nom de la société s'affiche ici
        societeType: entityType, // 🟢 Pour savoir si c'est un client ou un fournisseur
        commentaire: data.commentaire || ''
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [contact_id]);

  useEffect(() => { 
    fetchContact(); 
  }, [fetchContact]);

  const handleInputChange = (field, value) => {
    setContact(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!contact.nom) {
      setError('Le nom est requis');
      return;
    }
    const service = new ContactService();
    setLoading(true);
    try {
      const payload = {
        id: parseInt(contact_id, 10),
        nom: contact.nom,
        prenom: contact.prenom,
        email1: contact.email1,
        email2: contact.email2,
        telephone1: contact.telephone1,
        telephone2: contact.telephone2,
        commentaire: contact.commentaire,
        // 🟢 On renvoie l'objet imbriqué avec l'ID si on veut permettre la mise à jour de la liaison
        // (Note: si vous ne changez pas l'entité liée ici, le backend gardera l'existante)
        ...(contact.clientId ? { client: { id: contact.clientId } } : {}),
        ...(contact.fournisseurId ? { fournisseur: { id: contact.fournisseurId } } : {})
      };
      
      await service.updateContact(contact_id, payload);
      alert('Contact mis à jour avec succès');
      setIsEditing(false);
      await fetchContact(); // Recharger pour avoir les données fraîches
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Supprimer définitivement ce contact ?')) return;
    try {
      await new ContactService().deleteContact(contact_id);
      navigate('/contact_list');
    } catch (err) {
      alert(err.message);
    }
  };

  if (loading) return <div className="containerMain contact-container"><div className="contact-status contact-status-loading">Chargement…</div></div>;
  if (error) return <div className="containerMain contact-container"><div className="contact-status contact-status-error">{error}</div></div>;

  return (
    <div className="containerMain contact-container">
      <div className="contact-header">
        <div className="contact-header-left">
          <h1 className="contact-title">Contact N°{contact.id || contact_id}</h1>
          <p className="contact-protected">Ceci est un contenu protégé</p>
        </div>
        <div className="contact-header-right">
          <button className="contact-btn contact-btn-back" onClick={() => navigate('/contact_list')}>← Retour</button>
          {!isEditing ? (
            <button className="contact-btn contact-btn-edit" onClick={() => setIsEditing(true)}>✏️ Éditer</button>
          ) : (
            <>
              <button className="contact-btn contact-btn-cancel" onClick={() => { setIsEditing(false); fetchContact(); }}>Annuler</button>
              <button className="contact-btn contact-btn-save" onClick={handleSave} disabled={loading}>{loading ? '...' : '💾 Enregistrer'}</button>
            </>
          )}
          <button className="contact-btn contact-btn-delete" onClick={handleDelete}>🗑️ Supprimer</button>
        </div>
      </div>

      <div className="contact-form-grid">
        <fieldset className="contact-fieldset" disabled={!isEditing}>
          <legend>Informations Personnelles</legend>
          <div className="contact-form-row">
            <label>Nom *<input type="text" value={contact.nom || ''} onChange={(e) => handleInputChange('nom', e.target.value)} required /></label>
          </div>
          <div className="contact-form-row">
            <label>Prénom<input type="text" value={contact.prenom || ''} onChange={(e) => handleInputChange('prenom', e.target.value)} /></label>
          </div>
          <div className="contact-form-row">
            <label>Email 1<input type="email" value={contact.email1 || ''} onChange={(e) => handleInputChange('email1', e.target.value)} /></label>
          </div>
          <div className="contact-form-row">
            <label>Email 2<input type="email" value={contact.email2 || ''} onChange={(e) => handleInputChange('email2', e.target.value)} /></label>
          </div>
          <div className="contact-form-row">
            <label>Téléphone 1<input type="tel" value={contact.telephone1 || ''} onChange={(e) => handleInputChange('telephone1', e.target.value)} /></label>
          </div>
          <div className="contact-form-row">
            <label>Téléphone 2<input type="tel" value={contact.telephone2 || ''} onChange={(e) => handleInputChange('telephone2', e.target.value)} /></label>
          </div>
        </fieldset>

        <fieldset className="contact-fieldset" disabled={!isEditing}>
          <legend>Informations Entreprise & Autres</legend>
          <div className="contact-form-row">
            <label>
              {/* 🟢 Le label s'adapte dynamiquement : "Client", "Fournisseur" ou "Société" */}
              {contact.societeType !== 'Aucun' ? contact.societeType : 'Société'}
              <input 
                type="text" 
                value={contact.societe || ''} 
                onChange={(e) => handleInputChange('societe', e.target.value)} 
                // Astuce : si vous ne voulez pas que l'utilisateur modifie ce champ manuellement 
                // (car changer l'entité liée nécessite un ID, pas juste un texte), décommentez la ligne suivante :
                // readOnly 
              />
            </label>
          </div>
          <div className="contact-form-row">
            <label>Commentaire<textarea value={contact.commentaire || ''} onChange={(e) => handleInputChange('commentaire', e.target.value)} rows="4" /></label>
          </div>
        </fieldset>
      </div>
    </div>
  );
}